import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { WmButton } from "@watermarkinsights/ripple-react";
import LOGO from "../../../images/icons/favicon-96.png";
import { cohortSelector, createCsvZip, createSubmissionCsv, downloadSubmissionPdf, getZipExport, getSignedUrlForZip, } from "../CohortSlice";
import { PdfRequirementView } from "./PdfRequirementView";
import { PdfRequirementDetailView } from "./PdfRequirementDetailView";
import { format } from "date-fns";
import LZString from "lz-string";
export const LPReqExportPage = () => {
    const decompressedString = LZString.decompress(localStorage.getItem("lpReqExportPayload") || "");
    const { cohortTitle, timelineTitle, exportType, exportSettings, flatRequirementDetails = [], selectedStudentsSubmissions = [], progUuid, cohortUuid, } = JSON.parse(decompressedString || "{}");
    const dispatch = useDispatch();
    const pdfHeaderRef = useRef(null);
    const pdfContainerRef = useRef(null);
    const pdfFooterRef = useRef(null);
    const [showPdfView, setShowPdfView] = useState(exportType === "PDF");
    const [extractInterval, setExtractInterval] = useState(null);
    document.title = "Exports | Watermark";
    const currentTabWindowRef = useRef(window);
    const currentTabOpenerRef = useRef(currentTabWindowRef.current.opener);
    // check safari browser
    const userAgent = navigator.userAgent.toLowerCase();
    const isSafariBrowserRef = useRef(userAgent.includes("safari") && !userAgent.includes("chrome"));
    const { lpReqExportDetailsCsv, lpReqExportRejected, lpReqPdfExportDetails, lpReqPdfExportRejected, lpReqCsvZipExportDetails, lpReqCsvZipExportRejected, userExtract, } = useSelector(cohortSelector);
    const errorState = lpReqExportRejected ||
        lpReqPdfExportRejected ||
        lpReqCsvZipExportRejected ||
        userExtract.status === "FAILED";
    const primaryText = useIntl().formatMessage({
        id: "app.programApplications.lp.requirements.export.page.primary.text",
    }, { exportType });
    const errorPrimaryText = useIntl().formatMessage({
        id: "app.programApplications.lp.requirements.export.page.error.primary.text",
    }, { exportType });
    const secondaryText = useIntl().formatMessage({
        id: "app.programApplications.lp.requirements.export.page.secondary.text",
    }, { exportType });
    const errorSecondaryText = useIntl().formatMessage({
        id: "app.programApplications.lp.requirements.export.page.error.secondary.text",
    }, { exportType });
    const helpText = useIntl().formatMessage({
        id: "app.programApplications.lp.requirements.export.page.help.text",
    });
    const buttonText = useIntl().formatMessage({
        id: "app.programApplications.lp.requirements.export.cancel.export.text",
    });
    const showDetailPdfView = exportSettings === null || exportSettings === void 0 ? void 0 : exportSettings.includes("INCLUDE_REQUIREMENT");
    const csvZipExport = exportSettings === null || exportSettings === void 0 ? void 0 : exportSettings.includes("INCLUDE_ZIP");
    const downloadCsv = () => {
        //Download Csv
        if (lpReqExportDetailsCsv) {
            // create a blob from csv data
            const blob = new Blob([lpReqExportDetailsCsv], {
                type: "text/csv",
            });
            // generate and download link
            const url = currentTabWindowRef.current.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${cohortTitle}.csv`;
            document.body.appendChild(a);
            a.click();
            // clean up the URL object
            currentTabWindowRef.current.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            const timeout = isSafariBrowserRef.current ? 10000 : 500;
            setTimeout(() => {
                // In Ipad Safari page unload event does not work
                if (isSafariBrowserRef.current) {
                    handleUnload();
                }
                currentTabWindowRef.current.close();
            }, timeout);
        }
    };
    const downloadPdf = () => {
        //Download PDF
        if (lpReqPdfExportDetails) {
            // create a blob from pdf data
            const blob = new Blob([lpReqPdfExportDetails], {
                type: "application/pdf",
            });
            // generate and download link
            const url = currentTabWindowRef.current.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            if (showDetailPdfView) {
                a.download = `${cohortTitle} Insights_and_Requirement_Overview.pdf`;
            }
            else {
                a.download = `${cohortTitle} Insights.pdf`;
            }
            document.body.appendChild(a);
            // In Ipad Safari page unload event does not work
            if (isSafariBrowserRef.current) {
                handleUnload();
            }
            a.click();
            // clean up the URL object
            currentTabWindowRef.current.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            setTimeout(() => {
                currentTabWindowRef.current.close();
            }, 500);
        }
    };
    const downloadCsvZip = () => {
        setTimeout(() => {
            if (lpReqCsvZipExportDetails) {
                currentTabWindowRef.current.open(lpReqCsvZipExportDetails, "_self");
                setTimeout(() => {
                    // In Ipad Safari page unload event does not work
                    if (isSafariBrowserRef.current) {
                        handleUnload();
                    }
                    currentTabWindowRef.current.close();
                }, 5000);
            }
        }, 2000);
    };
    const callCsvExportService = () => {
        let selectedRequirements = [];
        let reqCount = 0;
        const dispatchFn = csvZipExport ? createCsvZip : createSubmissionCsv;
        if (dispatchFn === createCsvZip) {
            dispatch(dispatchFn({
                selectedRequirements: flatRequirementDetails.reduce((selectedRequirements = [], detail) => {
                    var _a;
                    (_a = detail === null || detail === void 0 ? void 0 : detail.requirements) === null || _a === void 0 ? void 0 : _a.forEach((req) => {
                        ++reqCount;
                        selectedRequirements.push({
                            requirementUuid: req.uuid,
                            sortOrder: reqCount,
                        });
                    });
                    return selectedRequirements;
                }, []),
                selectedUserUuids: [
                    ...new Set(selectedStudentsSubmissions === null || selectedStudentsSubmissions === void 0 ? void 0 : selectedStudentsSubmissions.map((submissions) => submissions.userUuid)),
                ],
                orgUuid: localStorage.getItem("orgUuid"),
                progUuid,
                cohortUuid,
            }));
        }
        else {
            dispatch(dispatchFn({
                selectedRequirementUuids: flatRequirementDetails.reduce((allUuids, detail) => {
                    var _a;
                    (_a = detail === null || detail === void 0 ? void 0 : detail.requirements) === null || _a === void 0 ? void 0 : _a.forEach((req) => {
                        allUuids.push(req.uuid);
                    });
                    return allUuids;
                }, []),
                selectedUserUuids: [
                    ...new Set(selectedStudentsSubmissions === null || selectedStudentsSubmissions === void 0 ? void 0 : selectedStudentsSubmissions.map((submissions) => submissions.userUuid)),
                ],
                orgUuid: localStorage.getItem("orgUuid"),
                progUuid,
                cohortUuid,
            }));
        }
    };
    const handleUnload = () => {
        localStorage.removeItem("isLpExportTabOpen");
        currentTabOpenerRef.current.postMessage("lpReqTabClosed", "*");
    };
    useEffect(() => {
        localStorage.setItem("isLpExportTabOpen", "true");
        if (currentTabOpenerRef.current) {
            currentTabOpenerRef.current.postMessage("lpReqTabOpen", "*");
        }
        if (exportType === "CSV") {
            callCsvExportService();
        }
        currentTabWindowRef.current.addEventListener("beforeunload", handleUnload);
        return () => currentTabWindowRef.current.removeEventListener("beforeunload", handleUnload);
    }, []);
    useEffect(() => {
        setTimeout(() => {
            if (exportType === "CSV") {
                csvZipExport ? downloadCsvZip() : downloadCsv();
            }
            if (exportType === "PDF") {
                downloadPdf();
            }
        }, 2000);
    }, [lpReqExportDetailsCsv, lpReqPdfExportDetails, lpReqCsvZipExportDetails]);
    useEffect(() => {
        if (showPdfView &&
            pdfContainerRef.current &&
            pdfHeaderRef.current &&
            pdfFooterRef.current) {
            dispatch(downloadSubmissionPdf({
                header: pdfHeaderRef.current.innerHTML,
                content: pdfContainerRef.current.innerHTML,
                footer: pdfFooterRef.current.innerHTML,
                version: process.env.RippleVersion,
            }));
            setShowPdfView(false);
        }
    }, [showPdfView]);
    useEffect(() => {
        if (userExtract.status === "FAILED" || userExtract.status === "CANCELLED") {
            clearInterval(extractInterval);
        }
        else if (userExtract.status === "PENDING") {
            const id = setInterval(() => {
                dispatch(getZipExport({
                    cohortUuid: cohortUuid,
                    orgUuid: localStorage.getItem("orgUuid"),
                    progUuid: progUuid,
                    extractUuid: userExtract.uuid,
                }));
            }, 1000);
            setExtractInterval(id);
            return () => {
                clearInterval(extractInterval);
            };
        }
        else if (userExtract.status === "COMPLETED") {
            clearInterval(extractInterval);
            dispatch(getSignedUrlForZip({
                cohortUuid: cohortUuid,
                orgUuid: localStorage.getItem("orgUuid"),
                progUuid: progUuid,
                extractUuid: userExtract.uuid,
            }));
        }
    }, [userExtract.uuid, userExtract.status]);
    return (_jsxs(_Fragment, { children: [_jsxs("div", Object.assign({ className: "lp-export-message-page" }, { children: [_jsx("img", { className: "export-message-logo", src: LOGO, alt: "Watermark logo" }), _jsx("span", Object.assign({ className: `lp-primary-text${errorState ? " lp-error" : ""}` }, { children: errorState ? errorPrimaryText : primaryText })), _jsx("div", Object.assign({ className: `lp-secondary-text-container ${errorState ? "lp-error-text" : ""}` }, { children: _jsx("span", Object.assign({ className: "lp-secondary-text" }, { children: errorState ? (errorSecondaryText) : (_jsxs(_Fragment, { children: [secondaryText, _jsx("b", { children: helpText })] })) })) })), !errorState && (_jsx(WmButton, Object.assign({ buttonType: "primary", id: "lp-cancel-export", onClick: () => {
                            // In Ipad Safari page unload event does not work
                            if (isSafariBrowserRef.current) {
                                handleUnload();
                            }
                            currentTabWindowRef.current.close();
                        }, placeholder: undefined }, { children: buttonText })))] })), showPdfView && (_jsxs("div", Object.assign({ 
                // keep this style as commented for debug pdf view on ui
                style: {
                    display: "none",
                    // position: "relative",
                    // zIndex: 1,
                    // background: "white",
                    // overflowY: "auto",
                    // height: "100vh",
                } }, { children: [_jsx("div", Object.assign({ ref: pdfHeaderRef }, { children: _jsx(PdfHeader, { title: timelineTitle, showDetailPdfView: showDetailPdfView }) })), _jsx("div", Object.assign({ ref: pdfContainerRef, style: {
                            padding: "20px 72px",
                        } }, { children: showDetailPdfView ? (_jsx(PdfRequirementDetailView, { flatRequirementDetails: flatRequirementDetails, selectedStudentsSubmissions: selectedStudentsSubmissions, cohortTitle: cohortTitle })) : (_jsx(PdfRequirementView, { flatRequirementDetails: flatRequirementDetails, selectedStudentsSubmissions: selectedStudentsSubmissions, cohortTitle: cohortTitle })) })), _jsx("div", Object.assign({ ref: pdfFooterRef }, { children: _jsx(PdfFooter, {}) }))] })))] }));
};
const orgTitle = localStorage.getItem("orgName");
const PdfHeader = ({ title, showDetailPdfView }) => {
    const headerPrimaryText = useIntl().formatMessage({
        id: "app.lp.reqs.export.pdf.header.primary.text",
    });
    const headerSecondaryText = useIntl().formatMessage({
        id: "app.lp.reqs.export.pdf.header.secondary.text",
    });
    const headerDetailedSecondaryText = useIntl().formatMessage({
        id: "app.lp.reqs.detailed.export.pdf.header.secondary.text",
    });
    const secondaryText = showDetailPdfView
        ? headerDetailedSecondaryText
        : headerSecondaryText;
    return (_jsxs("div", Object.assign({ style: {
            display: "flex",
            flexDirection: "column",
            padding: "20px 72px 0px 72px",
        } }, { children: [_jsx("div", Object.assign({ style: { fontSize: "14px", fontWeight: 400 } }, { children: orgTitle })), _jsx("div", Object.assign({ style: { fontSize: "14px", fontWeight: 600, marginTop: "6px" } }, { children: `${title} ${secondaryText}` }))] })));
};
const PdfFooter = () => {
    const footerCenterText = useIntl().formatMessage({
        id: "app.lp.reqs.detailed.export.pdf.footer.center.text",
    });
    const footerRightFirstText = useIntl().formatMessage({
        id: "app.lp.reqs.detailed.export.pdf.footer.right.first.text",
    });
    const footerRightSecondText = useIntl().formatMessage({
        id: "app.lp.reqs.detailed.export.pdf.footer.right.second.text",
    });
    return (_jsxs("div", Object.assign({ style: {
            width: "100%",
            WebkitPrintColorAdjust: "exact",
            display: "flex",
            justifyContent: "space-between",
            padding: "0px 72px 20px 72px",
            fontWeight: 400,
            fontSize: "12px",
        } }, { children: [_jsx("span", { children: format(new Date(), "MM/dd/yyyy") }), _jsxs("div", Object.assign({ style: {
                    display: "flex",
                    gap: "5px",
                    alignItems: "center",
                } }, { children: [_jsx("span", { children: footerCenterText }), _jsx("svg", Object.assign({ style: {
                            width: "75px",
                            height: "14px",
                        }, viewBox: "0 0 674 111", preserveAspectRatio: "xMinYMin", version: "1.1", xmlns: "http://www.w3.org/2000/svg" }, { children: _jsx("g", Object.assign({ id: "logo", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: _jsxs("g", Object.assign({ id: "Page-1" }, { children: [_jsx("polygon", { id: "Fill-1", fill: "#592A97", points: "665.8351 34.517 665.3011 34.517 665.3011 28.243 663.4171 28.243 663.4171 27.718 667.7191 27.718 667.7191 28.243 665.8351 28.243" }), _jsx("polygon", { id: "Fill-2", fill: "#592A97", points: "673.3728 27.7173 671.3338 32.1173 669.2938 27.7173 668.7318 27.7173 668.7318 34.5163 669.2458 34.5163 669.2458 28.8053 671.0328 32.6893 671.6348 32.6893 673.4218 28.8053 673.4218 34.5163 673.9348 34.5163 673.9348 27.7173" }), _jsx("polygon", { id: "Fill-3", fill: "#C1B3DD", points: "47.9983 27.7177 71.9793 13.8587 71.9793 41.5747" }), _jsx("polygon", { id: "Fill-4", fill: "#592A97", points: "96.0007 27.7177 71.9797 13.8567 71.9797 41.5737" }), _jsx("polygon", { id: "Fill-5", fill: "#C1B3DD", points: "96.0007 55.455 96.0007 27.718 71.9797 41.575" }), _jsx("polygon", { id: "Fill-6", fill: "#C1B3DD", points: "96.0007 55.455 96.0007 27.718 71.9797 41.575" }), _jsx("polygon", { id: "Fill-7", fill: "#8785C3", points: "47.9979 -1.42108547e-14 47.9979 27.717 71.9789 13.856 47.9999 -1.42108547e-14" }), _jsx("polygon", { id: "Fill-8", fill: "#8785C3", points: "96.0007 55.455 71.9797 41.571 71.9797 69.266" }), _jsx("polygon", { id: "Fill-9", fill: "#C1B3DD", points: "96.0007 83.1425 96.0007 55.4555 71.9797 69.2665" }), _jsx("polygon", { id: "Fill-10", fill: "#8785C3", points: "71.9797 97.0009 71.9797 69.2659 47.9987 83.2299" }), _jsx("polygon", { id: "Fill-11", fill: "#592A97", points: "47.9979 110.853 47.9999 110.853 71.9789 96.992 47.9979 83.229" }), _jsx("polygon", { id: "Fill-12", fill: "#FFFFFF", points: "71.9797 41.5747 47.9987 27.7177 47.9987 55.4547" }), _jsx("polygon", { id: "Fill-13", fill: "#2A1A42", points: "47.9983 55.455 47.9983 27.718 24.0033 41.543 24.0033 69.366 47.9983 83.229 71.9793 69.266 71.9793 41.578" }), _jsx("polygon", { id: "Fill-14", fill: "#8785C3", points: "47.9983 27.7177 24.0033 13.8417 24.0033 41.5437" }), _jsx("polygon", { id: "Fill-15", fill: "#592A97", points: "0 27.7173 24.004 41.5433 24.004 13.8413" }), _jsx("polygon", { id: "Fill-16", fill: "#8785C3", points: "0.0018 55.4553 24.0038 69.3653 24.0038 41.5433" }), _jsx("polygon", { id: "Fill-17", fill: "#C1B3DD", points: "0.0018 83.034 24.0038 69.366 0.0018 55.455" }), _jsx("polygon", { id: "Fill-18", fill: "#592A97", points: "0.0018 83.034 24.0038 96.996 24.0038 96.82 24.0038 69.366" }), _jsx("polygon", { id: "Fill-19", fill: "#C1B3DD", points: "24.0036 96.9961 47.9986 83.2291 24.0036 69.3661" }), _jsx("path", { d: "M625.1838,49.2959 L644.3468,27.7169 L658.2048,27.7169 L638.6738,49.2759 L661.7968,83.2209 L647.8298,83.2209 L630.8138,58.0649 L625.1838,64.3709 L625.1838,83.2209 L613.0718,83.2209 L613.0718,7.9289 L625.1838,7.9289 L625.1838,49.2959 Z M550.0088,60.9479 C550.0088,67.2509 545.5178,73.4089 537.5688,73.4089 C530.7298,73.4089 527.2428,70.4689 527.2428,65.9979 C527.2428,62.1389 530.0188,60.1129 536.1558,59.4379 C539.1088,59.1129 550.0088,57.9419 550.0088,57.9419 L550.0088,60.9479 Z M539.5038,26.4219 C524.4708,26.4219 517.2668,34.3079 517.2668,44.9279 L528.8128,44.9279 C529.0288,42.6379 530.4508,37.8899 538.8938,37.8899 C548.4608,37.8899 550.0128,44.2669 550.0128,47.6439 L550.0128,47.8929 C550.0128,47.8929 540.9998,48.8589 535.3618,49.4679 C523.5378,50.7429 514.9448,56.1899 514.9448,66.3449 C514.9448,76.2549 522.3608,84.5099 535.2148,84.5099 C544.0888,84.5099 549.0928,79.7039 550.0158,78.5809 L550.0158,83.2199 L562.1248,83.2199 L562.1248,46.7209 C562.1248,37.3989 556.9488,26.4219 539.5038,26.4219 Z M269.8358,60.9479 C269.8358,67.2509 265.3448,73.4089 257.3958,73.4089 C250.5578,73.4089 247.0698,70.4689 247.0698,65.9979 C247.0698,62.1389 249.8458,60.1129 255.9828,59.4379 C258.9348,59.1129 269.8358,57.9419 269.8358,57.9419 L269.8358,60.9479 Z M259.3308,26.4219 C244.2978,26.4219 237.0948,34.3079 237.0948,44.9279 L248.6408,44.9279 C248.8558,42.6379 250.2778,37.8899 258.7218,37.8899 C268.2888,37.8899 269.8398,44.2669 269.8398,47.6439 L269.8398,47.8929 C269.8398,47.8929 260.8278,48.8589 255.1888,49.4679 C243.3658,50.7429 234.7718,56.1899 234.7718,66.3449 C234.7718,76.2549 242.1878,84.5099 255.0418,84.5099 C263.9148,84.5099 268.9198,79.7039 269.8438,78.5809 L269.8438,83.2199 L281.9528,83.2199 L281.9528,46.7209 C281.9528,37.3989 276.7758,26.4219 259.3308,26.4219 Z M604.6088,38.6929 L604.6088,27.2269 L600.8068,27.2269 C582.4318,27.2269 578.2188,38.7089 578.2188,49.5939 L578.2188,83.2199 L590.3338,83.2199 L590.3338,50.9549 C590.3338,44.8269 593.4578,38.6929 601.6278,38.6929 L604.6088,38.6929 Z M418.3548,38.6929 L418.3548,27.2269 L414.5528,27.2269 C396.1778,27.2269 391.9658,38.7089 391.9658,49.5939 L391.9658,83.2199 L404.0808,83.2199 L404.0808,50.9549 C404.0808,44.8269 407.2048,38.6929 415.3748,38.6929 L418.3548,38.6929 Z M177.0258,27.7169 L177.0258,60.7829 C177.0258,68.8809 172.7738,73.0449 166.3348,73.0449 C160.0978,73.0449 155.6528,69.1719 155.6528,60.7829 L155.6528,27.7169 L143.5388,27.7169 L143.5388,61.4919 C143.5388,78.5939 155.9088,84.5099 166.1268,84.5099 C175.2058,84.5099 181.4208,79.1699 183.0678,76.6579 C184.7248,79.1699 190.9068,84.5099 200.0088,84.5099 C210.2268,84.5099 222.5968,78.5939 222.5968,61.4919 L222.5968,27.7169 L210.4828,27.7169 L210.4828,60.7979 C210.4828,69.1879 206.0478,73.0599 199.8098,73.0599 C193.5718,73.0599 189.1368,69.1879 189.1368,60.7979 L189.1368,27.7169 L177.0258,27.7169 Z M470.3248,83.2209 L470.3248,50.1399 C470.3248,41.7499 474.7588,37.8779 480.9978,37.8779 C487.2358,37.8779 491.6698,41.7499 491.6698,50.1399 L491.6698,83.2209 L503.7838,83.2209 L503.7838,49.4449 C503.7838,32.3429 491.4138,26.4269 481.1958,26.4269 C472.0948,26.4269 465.9128,31.7669 464.2548,34.2789 C462.6088,31.7669 456.3938,26.4269 447.3148,26.4269 C437.0968,26.4269 424.7268,32.3429 424.7268,49.4449 L424.7268,83.2209 L436.8398,83.2209 L436.8398,50.1549 C436.8398,41.7649 441.2848,37.8929 447.5228,37.8929 C453.9618,37.8929 458.2128,42.0569 458.2128,50.1549 L458.2128,83.2209 L470.3248,83.2209 Z M311.9318,66.1719 C311.9318,70.7599 314.5558,72.7009 318.8728,72.7009 L322.8998,72.7009 L322.8998,83.2199 L315.5518,83.2199 C305.6418,83.2199 299.8198,77.2359 299.8198,68.8999 L299.8198,38.2019 L290.0958,38.2019 L290.0958,27.7169 L299.8198,27.7169 L299.8198,15.1329 L311.9318,15.1329 L311.9318,27.7169 L322.8998,27.7169 L322.8998,38.2019 L311.9318,38.2019 L311.9318,66.1719 Z M342.3848,48.3389 L342.3848,49.4579 L365.0848,49.4579 L365.0848,48.3649 C365.0848,43.7529 362.0758,37.8959 353.7398,37.8959 C345.2638,37.8959 342.3848,43.6789 342.3848,48.3389 Z M364.3188,65.8069 L376.2708,65.8069 C375.5748,70.1549 372.2328,84.4499 353.7398,84.5099 C336.0388,84.5669 330.6148,71.1489 330.6148,60.0649 L330.6148,51.2589 C330.6148,39.4619 336.3008,26.4269 353.7398,26.4269 C372.6408,26.4269 376.8648,41.4689 376.8648,51.2589 L376.8648,59.2849 L342.3848,59.2849 L342.3848,61.2769 C342.3848,68.0129 345.6118,73.0429 353.7398,73.0429 C360.3748,73.0429 363.8418,68.8869 364.3188,65.8069 Z", id: "Fill-20", fill: "#592A97" })] })) })) }))] })), _jsxs("div", { children: [footerRightFirstText, " ", _jsx("span", { className: "pageNumber" }), " ", footerRightSecondText, " ", _jsx("span", { className: "totalPages" })] })] })));
};
